/* istanbul ignore file */

import React, { useEffect, useState } from 'react';
import { batch, connect, ConnectedProps } from 'react-redux';
import { getExperiments } from '@noths/polaris-client-experiments';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { Router } from '@noths/polaris-client-next-redux';
import { Media } from '@noths/polaris-client-responsive';
import {
  grid,
  maxWidthContainer,
  NotificationInfo,
} from '@noths/polaris-client-ribbons-design-system';
import { useRichRelevance } from '@noths/polaris-client-rich-relevance';
import { Breakpoint } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  getOrSetElevaateSessionId,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
} from '@noths/polaris-client-user-configuration';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parse } from 'url';

import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumb';
import { ListingsApiErrorGuardContainer } from 'src/components/molecules/ListingsApiErrorGuard';
import { FilterContainer } from 'src/components/organisms/Filter';
import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { QuickFiltersContainer } from 'src/components/organisms/Filter/containers/QuickFiltersContainer';
import { FilterMenuContainer } from 'src/components/organisms/FilterMenu';
import { HeaderCopyBlockContainer } from 'src/components/organisms/HeaderCopyBlock';
import { ProductPagesContainer } from 'src/components/organisms/ProductPage';
import { DELIVERY_QUICK_FILTERS_TITLE, REDIRECT_NO_LONGER_AVAILABLE } from 'src/constants/copy';
import { ERROR_CATEGORY_NOT_FOUND } from 'src/constants/errors';
import { useFilterNavType } from 'src/hooks/useFilterNavType';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation';
import { ProductsState, requestProducts } from 'src/redux/products';
import { selectPagePropsForClientHydration, selectPaginationTagValues } from 'src/redux/selectors';
import { AppThunkDispatch, getStore, useStore } from 'src/redux/store';
import * as contentstackService from 'src/services/contentstack';
import * as redirection from 'src/services/redirection';
import * as styles from 'src/styles/productList.styles';
import { NextConfig, PageProps, PageType } from 'src/types';
import * as elementIds from '../constants/elementIds';
const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const mapStateToProps = (state: ReduxApplicationState) => ({
  products: state.products,
  paginationTagValues: selectPaginationTagValues(state),
});

const connector = connect(mapStateToProps);

type CategoryPageProps = ProductsState & PageProps & ConnectedProps<typeof connector>;

const CategoryPage = ({
  products,
  paginationTagValues,
  updatedReduxState,
  functionalCookiesConsented,
}: CategoryPageProps) => {
  const { query } = useRouter();
  const { dispatch } = useStore();
  const [redirectNotification, setRedirectNotification] = useState(false);

  const {
    category: { id, metaTagDescription, canonicalTag, pageTitle, indexable },
  } = products;

  useRichRelevance(
    {
      API_KEY: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
      endpoint: publicRuntimeConfig.RICH_RELEVANCE_URL,
      sessionId: updatedReduxState?.userConfiguration?.richRelevanceSessionId,
      hasDisabledCookies: !functionalCookiesConsented,
    },

    () => {
      window.R3_COMMON.addPlacementType('category_page');
      window.R3_CATEGORY = new window.r3_category();
      window.R3_CATEGORY.setId(id.toString());
    },
  );

  const [prevMetaTag, nextMetaTag] = paginationTagValues;

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaTagDescription} />
        <link rel="canonical" href={canonicalTag} />
        {!indexable && <meta name="robots" content="noindex" />}
        {prevMetaTag && <link rel="prev" href={prevMetaTag} />}
        {nextMetaTag && <link rel="next" href={nextMetaTag} />}
        <link rel="preconnect" href={`${publicRuntimeConfig.hosts.elevaate}`} />
      </Head>
      <span
        dangerouslySetInnerHTML={{
          __html: `<!-- For Site Spect --><!-- 'pageGroup', 'category_listing_page' --><!-- End For Site Spect -->`,
        }}
      />
      <ListingsApiErrorGuardContainer>
        <div css={styles.wrapper}>
          <div css={[grid, maxWidthContainer]}>
            <div css={styles.breadcrumbContainer}>
              <BreadcrumbContainer />
              {redirectNotification && (
                <NotificationInfo>{REDIRECT_NO_LONGER_AVAILABLE}</NotificationInfo>
              )}
              <HeaderCopyBlockContainer />
            </div>
          </div>
          <div id={elementIds.PRODUCT_CONTAINER_WRAPPER}>
            <FilterMenuContainer />
            <Media lessThan={Breakpoint.LARGE}>
              <QuickFiltersContainer
                id={elementIds.DELIVERY_QUICK_FILTERS_NAME}
                title={DELIVERY_QUICK_FILTERS_TITLE}
                filterUid={FilterUid.Delivery}
              />
            </Media>
            <div css={[grid, maxWidthContainer, styles.productListWrapper]}>
              <div css={styles.productListSidebar}>
                <FilterContainer />
              </div>
              <ProductPagesContainer />
            </div>
          </div>
        </div>
      </ListingsApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, res, req }) => {
  const { category, ...queryParams } = query;

  if (Array.isArray(category)) {
    const pathname = parse(req.url!).pathname!;
    const routerState: Router = { pathname, query: queryParams };
    const { dispatch, getState } = getStore({
      router: routerState,
    });
    const {
      currency,
      delivery_zone,
      _elevaate_session_id,
      cognitoId,
      Loop54User,
      rr_session,
    } = req.cookies;
    const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
    const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
    const experiments = (req && getExperiments(req)) ?? '';
    const {
      currency: initialCurrency,
      deliveryZone: initialDeliveryZone,
    } = parseLocaleSetCookieHeaders(res.getHeaders());

    batch(() => {
      dispatch(navigationActions.setPageType(PageType.Category));
      dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
      dispatch(userConfigurationActions.setCurrencyCode(initialCurrency ?? currency));
      dispatch(userConfigurationActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
      dispatch(userConfigurationActions.setCognitoId(cognitoId));
      dispatch(userConfigurationActions.setExperiments(experiments));
      dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));

      if (functionalCookiesConsented) {
        const elevaateSessionId = getOrSetElevaateSessionId(res, _elevaate_session_id);
        const loop54UserId = getOrSetLoop54UserId(res, Loop54User);

        dispatch(userConfigurationActions.setElevaateSessionId(elevaateSessionId));
        dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
      }
    });

    const productsQuery = {
      ...queryParams,
      path: pathname,
    };

    const urlPath = req.url;
    const [, [navData, topBannerData]] = await Promise.all([
      (dispatch as AppThunkDispatch)(
        requestProducts({
          cookie: req.headers.cookie,
          query: productsQuery,
          isClientRequest: false,
        }),
      ),
      contentstackService.fetchData(req, experiments, urlPath),
    ]);

    const state = getState();
    const { products } = state;

    const redirect = redirection.productGuard(products, query, pathname);

    if (redirect) {
      return redirect;
    }

    if (products.error) {
      res.statusCode = products.error.name === ERROR_CATEGORY_NOT_FOUND ? 404 : 500;
      if (res.statusCode === 500) {
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
      }

      const props: PageProps = {
        navData,
        functionalCookiesConsented,
        updatedReduxState: {
          // Cherry pick state to be updated on the client
          products,
          navigation: { pageType: PageType.Category },
        },
      };

      return {
        props,
      };
    }

    const props: PageProps = {
      navData,
      topBannerData,
      functionalCookiesConsented,
      ...selectPagePropsForClientHydration(state),
    };

    return {
      props,
    };
  } else {
    const props: PageProps = {
      updatedReduxState: {
        products: {
          error: {
            message: 'An unknown error occurred',
          },
        },
      },
    };

    return {
      props,
    };
  }
};

export default connector(CategoryPage);
